import React from 'react';
import cx from 'classnames';

import {FontVariant} from '../../../../design-tokens/constants';
import type {ColorScheme} from '../../../types/variants';
import {ThemeConsumer} from '../../../themes/ThemeContext';
import {Text} from '../Text/Text';

import styles from './ToggleSwitch.module.scss';

export interface ToggleSwitchDisplayProps extends React.HTMLAttributes<HTMLElement> {
    as?: React.ElementType;
    checked: boolean;
    children?: React.ReactNode;
    className?: string;
    isSmall?: boolean;
    label: string;
    labelClassName?: string;
    labelPosition?: 'start' | 'end';
    labelModifier?: 'hidden' | 'spaced';
    labelFontWeight?: 'normal' | 'bold';
    colorScheme?: ColorScheme;
}

/**
 * Presentational version of ToggleSwitch that renders only the visual toggle
 * indicator and label text — it does not render an `<input>` or manage any
 * interaction/state itself.
 *
 * Use this when the parent element (e.g. an AriaMenu item) already handles
 * interaction and you only need to display the toggle state.
 *
 * @example
 * <ToggleSwitchDisplay checked={isMuted} label="Mute all guests" isSmall />
 */
export const ToggleSwitchDisplay: React.FC<ToggleSwitchDisplayProps> = ({
    as: Component = 'div',
    checked,
    children,
    className,
    label,
    labelClassName,
    labelPosition = 'end',
    labelModifier,
    labelFontWeight,
    isSmall,
    colorScheme,
    ...props
}) => (
    <ThemeConsumer>
        {({colorScheme: defaultColorScheme}) => (
            <Component
                {...props}
                className={cx(
                    colorScheme ?? defaultColorScheme,
                    styles.container,
                    styles[colorScheme ?? defaultColorScheme],
                    labelPosition && styles[labelPosition],
                    {
                        [styles.small]: isSmall,
                        [styles.spaced]: labelModifier === 'spaced',
                    },
                    className,
                )}
            >
                {children}
                <div
                    className={cx(styles.toggle, {
                        [styles.checked]: checked,
                    })}
                >
                    <div className={styles.indicator} />
                </div>
                <Text
                    fontVariant={
                        labelFontWeight === 'bold'
                            ? FontVariant.BodyBold
                            : FontVariant.Body
                    }
                    className={cx(styles.label, labelClassName, {
                        [styles.hidden]: labelModifier === 'hidden',
                    })}
                >
                    {label}
                </Text>
            </Component>
        )}
    </ThemeConsumer>
);
